import BaseEllipsisMenu from 'components/BaseEllipsisMenu/BaseEllipsisMenu';
import BaseModal from 'components/Modals/BaseModal';
import ConfirmationModal from 'components/Modals/ConfirmationModal';
import { usePerms } from 'contexts/PermsContext';
import IpawsSubmissionResultModal from 'features/Weather/features/WeatherAlerts/features/AlertCreator/components/IpawsAlertFields/components/IpawsSubmissionResultModal/IpawsSubmissionResultModal';
import {
  ALERT_CREATOR_ACCESS_LEVELS,
  FEMA_PERMISSIONS,
} from 'features/Weather/features/WeatherAlerts/features/AlertCreator/components/IpawsAlertFields/config/Ipaws.config';
import { setFemaUpdateContext } from 'features/Weather/features/WeatherAlerts/features/AlertCreator/state/slices/alertCreatorSlice';
import {
  buildCancelResultMessage,
  buildSubmissionErrorMessage,
  INITIAL_SUBMISSION_MESSAGE,
} from 'features/Weather/features/WeatherAlerts/features/AlertCreator/utils/femaSubmissionResult.utils';
import { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { FaCopy } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useCancelFemaAlertMutation } from 'services/ipaws/ipaws.api';
import { isFemaCancelResponse } from 'types/femaMessage.types';
import {
  getIllegalCancelReason,
  getIllegalUpdateReason,
  getLatestVersion,
  getRecordHeadline,
  getRecordIdentifier,
} from '../utils/femaMessage.utils';
import {
  FEMA_ENVIRONMENT_BADGE,
  femaStoredAlertRowToThread,
  getThreadEnvironment,
} from '../utils/femaToAlert.adapter';
import CapXmlViewer from './CapXmlViewer';

/**
 * Renders the FEMA actions available from a shared alert card.
 *
 * `message_id` and `can_update` are the card fields that `femaToAlert.adapter.ts` sets
 * (`FemaAlertCardData`); the component reads both.
 *
 * @param {{ alert: { message_id?: string|number, rawFemaRecord?: object, femaThread?: object, can_update?: boolean, globalActionId?: number|string|null } }} props - FEMA alert data.
 * @returns {JSX.Element|null} FEMA options or null when the stored payload is unavailable.
 */
const FemaOptions = ({ alert }) => {
  const [showCancel, setShowCancel] = useState(false);
  const [reason, setReason] = useState('');
  const [showCapXml, setShowCapXml] = useState(false);
  const [copyStatus, setCopyStatus] = useState('idle');
  const [showResult, setShowResult] = useState(false);
  const [resultMessage, setResultMessage] = useState(INITIAL_SUBMISSION_MESSAGE);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const perms = usePerms();
  const [cancelFemaAlert, { isLoading: isCancelling }] = useCancelFemaAlertMutation();
  // Only a stored IPAWS message (femaThread / rawFemaRecord) carries an Ipaws_Message id that
  // Update and Cancel may act on. A legacy active-alerts row has none: its message_id is a
  // Custom_Alerts id, and cancelling by it would target an unrelated IPAWS message.
  const thread =
    alert.femaThread ??
    (alert.rawFemaRecord ? femaStoredAlertRowToThread(alert.rawFemaRecord) : null);
  const latest = thread ? getLatestVersion(thread) : undefined;

  if (!thread || !latest) return null;

  const canManageFemaAlerts = perms.testPermission(ALERT_CREATOR_ACCESS_LEVELS, [
    FEMA_PERMISSIONS.manage,
  ]);
  const illegalUpdateReason = getIllegalUpdateReason(thread);
  const illegalCancelReason = getIllegalCancelReason(thread);
  const canUpdate = alert.can_update ?? alert.rawFemaRecord?.can_update ?? true;
  const wasCreatedByGlobalAction =
    alert.globalActionId != null || alert.rawFemaRecord?.global_action_id != null;
  const capXml = latest.rawCapXml;
  // The one message the Cancel will name: the newest version of this alert. The confirmation
  // shows it, because nothing else in its history is touched (card BE-28).
  const messageToCancelLabel = `${getRecordHeadline(latest) ?? getRecordIdentifier(latest)} (${getRecordIdentifier(latest)})`;
  // The class the Cancel goes to. The backend takes it from the message row, so the dialog names
  // it when the row carries it. A row written before migration 010 has no class: no line shows.
  const threadEnvironment = getThreadEnvironment(thread);
  const cancelEnvironmentLabel = threadEnvironment
    ? FEMA_ENVIRONMENT_BADGE[threadEnvironment].label
    : null;

  const handleCopyCapXml = async () => {
    if (!capXml) return;

    try {
      await navigator.clipboard.writeText(capXml);
      setCopyStatus('success');
    } catch {
      setCopyStatus('error');
    }
  };

  const handleOpenCancel = () => {
    setReason('');
    setShowCancel(true);
  };

  // `POST /v2/fema/:id/cancel` sends ONE CAP Cancel for the ONE message it names (card BE-28):
  // earlier and later messages in this alert's history stay on the air, and the operator puts
  // content back with an Update. The body is `{ reason? }` — a `scope` is refused with a 400.
  const handleConfirmCancel = async () => {
    if (!canManageFemaAlerts || !canUpdate || illegalCancelReason || isCancelling) return;

    const trimmedReason = reason.trim();

    try {
      const body = await cancelFemaAlert({
        messageId: latest.id,
        ...(trimmedReason ? { reason: trimmedReason } : {}),
      }).unwrap();
      setShowCancel(false);
      setResultMessage(buildCancelResultMessage(body));
      setShowResult(true);
    } catch (error) {
      setShowCancel(false);
      // A 409 (FEMA refused the Cancel, or a precondition did) carries the same `result` shape as
      // a 200 — read it the same way. `axiosBaseQuery` puts the body under `raw`; a fetch-shaped
      // or mocked error carries it under `data`. Any other failure (400 findings, network error)
      // uses the generic builder.
      const refusalBody = error?.raw ?? error?.data;
      if (isFemaCancelResponse(refusalBody)) {
        setResultMessage(buildCancelResultMessage(refusalBody));
      } else {
        setResultMessage(
          buildSubmissionErrorMessage(
            error,
            'The cancel request failed.',
            'Cancel request failed.',
          ),
        );
      }
      setShowResult(true);
    }
  };

  const options = [
    {
      component: (
        <Button
          type="button"
          variant="link"
          className="text-start"
          onClick={() => setShowCapXml(true)}
        >
          View CAP XML
        </Button>
      ),
    },
    // ABSENT without ManageFemaAlerts — the backend enforces it strictly for every access level,
    // so the frontend never disables the button instead of hiding it.
    !wasCreatedByGlobalAction && canManageFemaAlerts && canUpdate && !illegalUpdateReason
      ? {
          component: (
            <Button
              className="rounded-3"
              variant="warning"
              onClick={() => {
                dispatch(setFemaUpdateContext(latest));
                navigate('/publicuser/createalert');
              }}
            >
              Update
            </Button>
          ),
        }
      : null,
    !wasCreatedByGlobalAction && canManageFemaAlerts && canUpdate && !illegalCancelReason
      ? {
          component: (
            <Button
              type="button"
              variant="link"
              className="text-start text-danger"
              onClick={handleOpenCancel}
            >
              Cancel
            </Button>
          ),
        }
      : null,
  ].filter(Boolean);

  if (!options.length) return null;

  return (
    <div>
      <BaseEllipsisMenu ariaLabel={`Open FEMA options for ${alert.message_id}`} options={options} />
      <ConfirmationModal
        show={showCancel}
        handleShow={(show) => setShowCancel(show ?? false)}
        bodyTxt={
          <div className="text-start">
            <h4>Cancel this FEMA alert?</h4>
            <p className="mb-2">
              This cancels only this message. Earlier and later messages in its history stay on the
              air. To put content back afterwards, send an Update.
            </p>
            <ul className="mb-2">
              <li>{messageToCancelLabel}</li>
            </ul>
            {cancelEnvironmentLabel ? (
              <p className="mb-2" data-testid="fema-cancel-environment">
                FEMA environment: <strong>{cancelEnvironmentLabel}</strong>
              </p>
            ) : null}
            <Form.Group controlId={`fema-cancel-reason-${alert.message_id}`} className="text-start">
              <Form.Label className="small mb-1">Reason (optional)</Form.Label>
              <Form.Control
                type="text"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </Form.Group>
          </div>
        }
        cancelBtnTxt="Keep message"
        confirmBtnTxt={isCancelling ? 'Cancelling...' : 'Cancel message'}
        confirmButtonVariant="danger"
        cancelHandler={() => setShowCancel(false)}
        confirmHandler={handleConfirmCancel}
      />
      <IpawsSubmissionResultModal
        show={showResult}
        onClose={() => setShowResult(false)}
        resultMessage={resultMessage}
      />
      <BaseModal
        show={showCapXml}
        handleShow={() => setShowCapXml(false)}
        title="CAP XML"
        size="lg"
        scrollable
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        footer={
          <div className="d-flex align-items-center gap-2 w-100">
            {copyStatus !== 'idle' ? (
              <span className="small text-body-secondary" role="status">
                {copyStatus === 'success' ? 'Copied' : 'Copy failed'}
              </span>
            ) : null}
            <div className="ms-auto d-flex gap-2">
              <Button type="button" variant="primary" onClick={handleCopyCapXml} disabled={!capXml}>
                <FaCopy aria-hidden="true" className="me-2" />
                Copy CAP XML
              </Button>
              <Button type="button" variant="primary" onClick={() => setShowCapXml(false)}>
                Close
              </Button>
            </div>
          </div>
        }
      >
        <CapXmlViewer xml={capXml} />
      </BaseModal>
    </div>
  );
};

export default FemaOptions;
